/**
 * The firm's choice of how its clients sign in — FR-J10, ADR-0028.
 *
 * **Written against 12E's declared API** (waves.md §4 "Wave 12", interface
 * I-4), not against a route in this worktree: `GET /api/v1/firm/sign-in` →
 * `{identifiers, allow_signup, providers}`, `PUT` the same shape, `400
 * VALIDATION_ERROR` on an empty `identifiers` or an unknown value. The tests
 * mock the route; the close's container pass is the first time the two meet.
 *
 * **ProDesk builds no sign-in screen** (CLAUDE.md §7, ADR-0004). This is the
 * firm's *choice*, published in `/site-brand.json` and honoured by the
 * platform's own `/accounts/` page (12P). Nothing here touches a credential.
 *
 * The pure helpers are beside the client so the page's rules — at least one
 * identifier, the mock following each switch, the hub's summary line — are
 * testable without rendering.
 */

import { api } from '../api'

export type Identifier = 'email' | 'mobile'
export type Provider = 'google' | 'microsoft'

export interface SignInChoice {
  identifiers: Identifier[]
  allow_signup: boolean
  providers: Provider[]
}

/** ADR-0028 §1: exactly what the page offered before the firm chose — so no
 *  firm's clients meet a change they did not ask for. Also what the page
 *  draws while the read is in flight or has failed. */
export const DEFAULT_SIGN_IN: SignInChoice = {
  identifiers: ['email', 'mobile'],
  allow_signup: true,
  providers: [],
}

export const signIn = {
  read: () => api<SignInChoice>('/api/v1/firm/sign-in'),
  save: (body: SignInChoice) => api<SignInChoice>('/api/v1/firm/sign-in', { method: 'PUT', body }),
}

/** The segmented control's three positions. At least one identifier is the
 *  rule (FR-J10), and a control with no *neither* position cannot break it. */
export type IdentifierMode = 'email' | 'mobile' | 'both'

export function identifierMode(choice: SignInChoice): IdentifierMode {
  const email = choice.identifiers.includes('email')
  const mobile = choice.identifiers.includes('mobile')
  if (email && mobile) return 'both'
  return mobile ? 'mobile' : 'email'
}

export function identifiersFor(mode: IdentifierMode): Identifier[] {
  return mode === 'both' ? ['email', 'mobile'] : [mode]
}

export function toggleProvider(choice: SignInChoice, provider: Provider, on: boolean): SignInChoice {
  const rest = choice.providers.filter((p) => p !== provider)
  return { ...choice, providers: on ? [...rest, provider] : rest }
}

/** Why a choice cannot be saved, or `null`. The server refuses the same
 *  things; this is so the firm is told before the request. */
export function signInProblem(choice: SignInChoice): string | null {
  if (choice.identifiers.length === 0) return 'Choose at least one way for clients to sign in.'
  return null
}

export const PROVIDER_LABELS: Record<Provider, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
}

/** What the mock sign-in card draws, as data — the page renders this and the
 *  tests read it, so "the mock follows each switch" is one function. */
export function mockCard(choice: SignInChoice): {
  fieldLabel: string
  placeholder: string
  providers: string[]
  showRegister: boolean
} {
  const mode = identifierMode(choice)
  return {
    fieldLabel:
      mode === 'both' ? 'Email or mobile number' : mode === 'email' ? 'Email' : 'Mobile number',
    placeholder:
      mode === 'both' ? 'you@example.com or 98765 43210' : mode === 'email' ? 'you@example.com' : '98765 43210',
    providers: choice.providers.map((p) => PROVIDER_LABELS[p]),
    showRegister: choice.allow_signup,
  }
}

/** The hub card's line — the close's wire (c), written here so the close only
 *  has to see it work. */
export function signInSummary(choice: SignInChoice): string {
  const mode = identifierMode(choice)
  const how = mode === 'both' ? 'Email or mobile' : mode === 'email' ? 'Email only' : 'Mobile only'
  const parts = [how, choice.allow_signup ? 'clients can sign up' : 'by invitation']
  if (choice.providers.length) {
    parts.push(choice.providers.map((p) => PROVIDER_LABELS[p]).join(' and '))
  }
  return parts.join(' · ')
}
